import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, type Movimiento, type Posicion, type Sector } from '../api';
import { useApi, useDemorado, VIVO } from '../lib/useApi';
import { useEsCelular } from '../lib/useMedia';
import { useEstadoGuardado } from '../lib/persistencia';
import { usePuedeOperar, useSesion } from '../lib/sesion';
import { Cargando, ChipBoton, Encabezado, Icono, Numero, PanelError } from '../components/ui';
import { aparecer, fila, pop, resorte, revelarDesde, subir } from '../components/movimiento';
import Isometrico from '../components/Isometrico';
import { PlanoGeneral } from './PlanoGeneral';
import { hayWebGL } from '../tres/soporte';
import {
  aviso,
  botonPrimario,
  botonSecundario,
  c,
  chip,
  enlace,
  mono,
  nota,
  tarjeta,
  tono as tonoDe,
  tonoDeTexto,
} from '../theme';

/**
 * La escena en WebGL, en un chunk aparte y cargado sólo cuando se entra a la
 * vista 3D. El `lazy` no es decorativo: `three` + `@react-three/fiber` +
 * `@react-three/drei` pesan más que toda la aplicación junta, y el escáner de
 * códigos, las etiquetas y el stock no tienen por qué pagarlos.
 */
const Escena3d = lazy(() => import('../tres/Escena3d'));

/**
 * `PlanoGeneral` se mudó a `views/PlanoGeneral.tsx` (este módulo había pasado
 * las mil líneas) pero se sigue **reexportando desde acá**, y no por comodidad:
 * `views/plano.check.tsx` y `demo-plano.tsx` lo importan de `./Racks`, y ese
 * import es lo que hace que `npm run check:plano` construya por SSR el módulo
 * entero de este archivo —o sea, la red que vigila que el chunk de Three siga
 * cargándose diferido y no se cuele en el bundle principal—. Si el check
 * dejara de tocar `Racks.tsx`, esa red desaparecería en silencio.
 */
export { PlanoGeneral };

/**
 * `revelarDesde` con el recorte soltado al llegar. El origen sale del dedo
 * (ver `toque` en `Racks`) y puede caer lejos de la caja que se revela —el
 * detalle mide distinto que el plano donde se tocó—: con el círculo del
 * preset (150 %) quedaba una esquina recortada en reposo. 1500 px tapa
 * cualquier panel de esta pantalla y, a diferencia de `none`, se puede
 * achicar de vuelta en la salida.
 */
const revelar = (x: string, y: string) => {
  const r = revelarDesde(x, y);
  return { ...r, animate: { ...r.animate, transitionEnd: { clipPath: `circle(1500px at ${x} ${y})` } } };
};

function VistaSector({
  sector,
  seleccion,
  onPosicion,
}: {
  sector: Sector;
  seleccion: string | null;
  onPosicion: (codigo: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* En el celular el uso, entre el nombre y el chip, quedaba partido en
          una columna de 90 px: nombre y ocupación arriba, el uso debajo a lo
          ancho. Desde la tablet, la fila de siempre. */}
      <div className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-0.5 tablet:flex tablet:flex-wrap tablet:gap-3">
        <span style={{ fontSize: 18, fontWeight: 600, color: c.texto }}>{sector.nombre}</span>
        <span className="col-span-2 row-start-2" style={{ fontSize: 13, color: c.texto3, flex: 1, minWidth: 0 }}>{sector.uso}</span>
        <span
          className="col-start-2 row-start-1 justify-self-end"
          style={{
            padding: '4px 11px',
            borderRadius: 999,
            background: c.fondo,
            border: `1px solid ${c.borde}`,
            fontSize: 12,
            fontWeight: 600,
            color: c.texto2,
          }}
        >
          <Numero valor={sector.pct} />% ocupado
        </span>
      </div>

      {sector.racks.map((r, i) => {
        const llenos = r.posiciones.reduce((a, p) => a + p.ocupados, 0);
        return (
          <motion.div key={r.id} {...fila(i)} style={{ border: `1px solid ${c.borde}`, borderRadius: 12, padding: '12px 14px', background: '#fdfdfd' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
              <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 600, color: c.texto }}>{r.id}</span>
              <span style={{ fontSize: 12, color: c.texto3, flex: 1, minWidth: 0 }}>
                {r.tipo === 'piso' ? (
                  <>
                    Zona de piso · <Numero valor={llenos} desdeCero={false} /> {llenos === 1 ? 'pallet' : 'pallets'}
                  </>
                ) : (
                  <>
                    {r.columnas} posiciones · {r.niveles} niveles · <Numero valor={llenos} /> de {r.columnas * r.niveles}{' '}
                    pallets
                  </>
                )}
              </span>
            </div>

            {/* En el celular, tres por renglón a lo ancho: con el ancho fijo de
                76 px quedaba un hueco a la derecha y botones más chicos que el
                dedo. Desde la tablet, como siempre. */}
            <div className="grid grid-cols-3 gap-1.5 tablet:flex tablet:flex-wrap">
              {r.posiciones.map((p) => {
                const sel = seleccion === p.codigo;
                return (
                  <button
                    key={p.codigo}
                    type="button"
                    onClick={() => onPosicion(p.codigo)}
                    aria-label={
                      p.zona ? `Zona de piso ${r.id}, ${p.ocupados} pallets` : `Posición ${p.codigo}, ${p.ocupados} de ${r.niveles} niveles ocupados`
                    }
                    className="tablet:w-[76px]"
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                      padding: '9px 6px',
                      borderRadius: 12,
                      cursor: 'pointer',
                      background: p.hit ? c.ambarSuave : p.ocupados ? c.verdeTinte : c.panel,
                      border:
                        p.ocupados === 0 && !p.hit
                          ? `1px dashed ${c.bordeFuerte}`
                          : `1px solid ${p.hit ? c.ambar : c.verdeBorde}`,
                      color: p.ocupados ? c.texto : c.texto4,
                    }}
                  >
                    {/* El anillo de la elegida es uno solo y viaja de la
                        posición anterior a la nueva, en vez de apagarse en una
                        y prenderse en otra. */}
                    {sel && (
                      <motion.span
                        layoutId="racks-posicion-elegida"
                        transition={resorte}
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          inset: -1,
                          borderRadius: 12,
                          boxShadow: '0 0 0 3px rgba(0,122,71,.28)',
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                    <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 600 }}>{p.codigo.split('-')[1]}</span>
                    <span style={{ display: 'flex', gap: 2 }}>
                      {p.niveles.map((n) => (
                        <span
                          key={n.nivel}
                          style={{
                            width: 14,
                            height: 9,
                            borderRadius: 3,
                            background: n.ocupado ? (p.hit ? c.ambar : c.verde) : c.panel,
                            border: n.ocupado ? '1px solid transparent' : `1px dashed ${c.bordeFuerte}`,
                            // CSS y no Motion: son cientos de cuadraditos y
                            // sólo cambian cuando el refresco trae otro dato.
                            transition: 'background-color .35s, border-color .35s',
                          }}
                        />
                      ))}
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

function Movimientos({ codigo, onVolver }: { codigo: string; onVolver: () => void }) {
  const { datos, cargando, error, recargar } = useApi(() => api.movimientos(codigo), [codigo], VIVO.normal);
  const items: Movimiento[] = datos ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          onClick={onVolver}
          aria-label="Volver al detalle"
          style={{
            width: 32,
            height: 32,
            flex: '0 0 32px',
            borderRadius: 999,
            border: `1px solid ${c.borde}`,
            background: c.panel,
            color: c.texto2,
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
          }}
        >
          <Icono nombre="arrow_back" size={19} />
        </button>
        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>Movimientos</span>
          <span style={{ fontFamily: mono, fontSize: 12, color: c.texto3 }}>{codigo}</span>
        </span>
      </div>

      {error ? (
        <PanelError mensaje={error} onReintentar={recargar} />
      ) : cargando && items.length === 0 ? (
        <Cargando alto={120} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {items.map((m, i) => {
            const t = tonoDe(tonoDeTexto(m.tono));
            return (
              <motion.div key={m.id} {...fila(i)} style={{ display: 'flex', gap: 12 }}>
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 28px' }}>
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      flex: '0 0 28px',
                      borderRadius: 999,
                      display: 'grid',
                      placeItems: 'center',
                      background: t.bg,
                      border: `1px solid ${t.fg}`,
                      color: t.fg,
                    }}
                  >
                    <Icono nombre={m.icono} size={15} />
                  </span>
                  {i !== items.length - 1 && <span style={{ flex: 1, width: 1, background: c.borde, margin: '4px 0' }} />}
                </span>

                <span style={{ flex: 1, minWidth: 0, paddingBottom: 16, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: c.texto }}>{m.tipo}</span>
                    <span style={{ flex: 1, minWidth: 0 }} />
                    <span style={{ fontFamily: mono, fontSize: 12, color: c.texto3, whiteSpace: 'nowrap' }}>
                      {m.fecha}
                    </span>
                  </span>
                  <span style={{ fontSize: 12.5, color: c.texto2, textWrap: 'pretty' }}>{m.detalle}</span>
                  <span style={{ fontSize: 12, color: c.texto4 }}>{m.operario}</span>
                </span>
              </motion.div>
            );
          })}
        </div>
      )}

      <div style={{ ...nota, borderRadius: 12 }}>
        Historial de la posición, no del pallet. Los movimientos de un pallet reubicado siguen en su posición nueva.
      </div>
    </div>
  );
}

export default function Racks({
  busquedaInicial,
  onUbicar,
  onVerPallet,
  onVaciarRack,
}: {
  busquedaInicial?: string | null;
  /** Abre la ficha del pallet (Fraccionamiento): su contenido y su historial. */
  onVerPallet?: (pallet: string) => void;
  /**
   * Lleva a Ubicar pallets, el único lugar donde se mueven: este mapa queda
   * para consultar y ver el historial. Con un pallet, llega ya elegido.
   */
  onUbicar?: (pallet?: string) => void;
  /** Abre Vaciar rack con este rack elegido; sólo se pasa si el rol tiene esa vista. */
  onVaciarRack?: (rack: string) => void;
}) {
  const puedeOperar = usePuedeOperar();
  const puedeVerPallet = useSesion().vistas.includes('frac');
  const celular = useEsCelular();

  /**
   * Dónde estaba: sector abierto, posición elegida, búsqueda y vista
   * sobreviven a un F5 (ver `lib/persistencia.ts`). Recargar adentro de un
   * sector, o en el 3D, dejaba a todo el mundo de vuelta en el plano general.
   *
   * **Salvo cuando el escaneo es nuevo.** Llegar desde el lector
   * (`#/racks/PLT-…`) dice qué se quiere ver *ahora*, y gana: el pallet recién
   * leído no puede aparecer tapado por el sector de hace un rato. Pero eso
   * vale una sola vez, no en cada recarga: el hash sigue teniendo el pallet
   * mientras el operario trabaja en el mapa, así que se guarda cuál fue el
   * último escaneo atendido y, si es el mismo, esto es un F5 y se retoma
   * dónde estaba. Un id que ya no existe —el sector se borró, la posición
   * cambió de nombre— no hace falta filtrarlo: más abajo se resuelve contra
   * los datos del mapa y cae solo en null.
   */
  const [escaneoAtendido, setEscaneoAtendido] = useEstadoGuardado<string | null>('racks.escaneo', null);
  const [escaneoNuevo] = useState(() => Boolean(busquedaInicial) && busquedaInicial !== escaneoAtendido);
  const sinRastro = () => !escaneoNuevo;

  const [texto, setTexto] = useEstadoGuardado('racks.busqueda', busquedaInicial ?? '', sinRastro);
  const q = useDemorado(texto, 300);
  const [sectorId, setSectorId] = useEstadoGuardado<string | null>('racks.sector', null, sinRastro);
  const [seleccion, setSeleccion] = useEstadoGuardado<string | null>('racks.posicion', null, sinRastro);

  useEffect(() => {
    setEscaneoAtendido(busquedaInicial ?? null);
  }, [busquedaInicial, setEscaneoAtendido]);
  const [verMovs, setVerMovs] = useState(false);
  // El plano de arriba es el que se usa a diario; la isométrica sirve para
  // ver la altura, que en planta no se distingue. El filtro del enum no es
  // decorativo: con cualquier otro string no se dibuja ninguna de las dos.
  const [vista, setVista] = useEstadoGuardado<'plano' | 'iso'>(
    'racks.vista',
    'plano',
    (v) => v === 'plano' || v === 'iso',
  );

  /**
   * El tercer camino, el que se olvida: WebGL arrancó bien y el navegador
   * pierde el contexto después (`webglcontextlost`) — puede pasar por quedarse
   * sin memoria de video o por un reinicio del driver gráfico, y en el
   * depósito hay equipos viejos. `three.js` ya avisa por consola
   * («WebGLRenderer: Context Lost.») pero no le dice nada al operario: sin
   * esto, la escena queda congelada o en blanco sin ninguna explicación. Con
   * la escena tapada por este aviso, al menos entiende qué pasó.
   *
   * **Por qué el listener vive acá y no en `Escena3d.tsx`:** ese archivo no es
   * de esta tarea. Y aunque lo fuera, un listener puesto directo en el
   * `<canvas>` no cambia nada de lo que sigue: `webglcontextlost` y
   * `webglcontextrestored` **no burbujean** (verificado a mano: un listener en
   * el contenedor del mapa no vio nada), así que la única forma de
   * escucharlos desde afuera del componente que crea el canvas es capturarlos
   * de camino hacia abajo. Un listener en `document` con el tercer argumento
   * en `true` (fase de **captura**) sí los recibe — la captura ocurre para
   * cualquier evento, burbujee o no; lo que el burbujeo apagado impide es sólo
   * la fase de vuelta—. Verificado con la extensión `WEBGL_lose_context` desde
   * la consola: perder el contexto dispara esto, y `three` solo restaura el
   * dibujo cuando el navegador restaura el contexto de verdad (no es un
   * defecto de esta pantalla: si el contexto no vuelve, no hay nada para
   * `Escena3d` que dibujar).
   */
  const [contextoPerdido, setContextoPerdido] = useState(false);
  useEffect(() => {
    const alPerder = () => setContextoPerdido(true);
    const alRestaurar = () => setContextoPerdido(false);
    document.addEventListener('webglcontextlost', alPerder, true);
    document.addEventListener('webglcontextrestored', alRestaurar, true);
    return () => {
      document.removeEventListener('webglcontextlost', alPerder, true);
      document.removeEventListener('webglcontextrestored', alRestaurar, true);
    };
  }, []);

  // Se refresca solo al mismo ritmo que Ubicar pallets: lo que un compañero
  // mueve allá aparece acá sin F5.
  const { datos, cargando, error, recargar } = useApi(() => api.mapa(q), [q], VIVO.normal);

  const sectores = datos?.sectores ?? [];
  const sector = sectorId ? (sectores.find((s) => s.id === sectorId) ?? null) : null;
  // El rack de la posición elegida: Vaciar rack trabaja por rack, no por posición.
  const rackDeLaPosicion = seleccion
    ? sectores.flatMap((s) => s.racks).find((r) => r.posiciones.some((p) => p.codigo === seleccion))
    : undefined;
  const posicion: Posicion | null = seleccion
    ? (sectores.flatMap((s) => s.racks).flatMap((r) => r.posiciones).find((p) => p.codigo === seleccion) ?? null)
    : null;

  // Un escaneo nuevo que cae en una sola posición entra directo a ella: no
  // tiene sentido mostrar la planta para que el operario toque lo único marcado.
  // Una sola vez: después se recorre el mapa como siempre.
  const entroAlEscaneo = useRef(false);
  useEffect(() => {
    if (entroAlEscaneo.current || !escaneoNuevo || !datos || q !== busquedaInicial || datos.hits !== 1) return;
    entroAlEscaneo.current = true;
    const duenio = datos.sectores.find((s) => s.hits > 0);
    const hit = duenio?.racks.flatMap((r) => r.posiciones).find((p) => p.hit);
    if (duenio && hit) {
      setSectorId(duenio.id);
      setSeleccion(hit.codigo);
    }
  }, [datos]); // eslint-disable-line react-hooks/exhaustive-deps

  // En el celular el detalle cae debajo del sector, fuera de la pantalla:
  // sin esto, tocar una posición parece no hacer nada.
  const detalle = useRef<HTMLDivElement>(null);

  /**
   * De dónde nace lo que se abre: el último lugar tocado de la pantalla. El
   * sector crece desde el punto del plano donde se lo tocó, y el detalle desde
   * la altura de la posición elegida. Se guarda en el momento de elegir y no
   * en cada render: si no, el refresco de cada pocos segundos lo movería.
   */
  const toque = useRef<{ x: number; y: number } | null>(null);
  const grilla = useRef<HTMLDivElement>(null);
  const lienzo = useRef<HTMLDivElement>(null);
  const [origenSector, setOrigenSector] = useState<[string, string]>(['50%', '0px']);
  const [origenDetalle, setOrigenDetalle] = useState('0px');
  /** El toque, relativo a `el` y adentro de su caja (`tope` para no irse muy abajo). */
  const relativo = (el: HTMLElement | null, corrido = 0, tope = Infinity): [string, string] | null => {
    const r = el?.getBoundingClientRect();
    if (!r || !toque.current) return null;
    const x = Math.min(Math.max(toque.current.x - r.left - corrido, 0), r.width);
    const y = Math.min(Math.max(toque.current.y - r.top - corrido, 0), r.height, tope);
    return [`${Math.round(x)}px`, `${Math.round(y)}px`];
  };
  const elegirPosicion = (codigo: string) => {
    // En la PC el detalle está a la derecha del mapa: nace del borde que da
    // al mapa, a la altura del dedo. Tope de 400 px: el detalle más corto
    // (una zona vacía) no llega mucho más abajo.
    setOrigenDetalle(relativo(grilla.current, 0, 400)?.[1] ?? '0px');
    setSeleccion(codigo);
  };
  const entrarASector = (id: string) => {
    // 16 px: el padding de la tarjeta, que el contenido no tiene.
    setOrigenSector(relativo(lienzo.current, 16) ?? ['50%', '0px']);
    setSectorId(id);
  };

  useEffect(() => {
    if (celular && posicion) detalle.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [posicion?.codigo]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Los siete props de la vista 3D, en un solo lugar: los consumen la escena
   * de WebGL y el respaldo en SVG, que tienen **la misma firma** justamente
   * para poder cambiarse uno por el otro. Duplicarlos en las dos ramas del
   * `hayWebGL()` es duplicar la fuente de la verdad.
   */
  const vista3d = {
    predio: datos?.predio ?? { nombre: 'Depósito', ancho: 60, largo: 40 },
    naves: datos?.naves ?? [],
    sectores,
    muelles: datos?.muelles ?? [],
    caminos: datos?.caminos ?? [],
    seleccion,
    // Tocar un hueco entra a su sector, además de seleccionar la posición.
    onPosicion: (codigo: string) => {
      const duenio = sectores.find((s) => s.racks.some((r) => r.posiciones.some((p) => p.codigo === codigo)));
      if (duenio) entrarASector(duenio.id);
      elegirPosicion(codigo);
    },
  };

  const crumb = {
    padding: '7px 14px',
    borderRadius: 999,
    border: `1px solid ${sector ? c.borde : c.verde}`,
    background: sector ? c.panel : c.verdeSuave,
    color: sector ? c.texto2 : c.verde,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  } as const;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
      onPointerDownCapture={(e) => (toque.current = { x: e.clientX, y: e.clientY })}
    >
      <Encabezado
        bajada="Planta general, sectores, racks y posiciones. Buscá un pallet y el mapa te marca dónde está."
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '0 16px',
            height: 48,
            background: c.panel,
            border: `1px solid ${c.bordeFuerte}`,
            borderRadius: 999,
            flex: 1,
            minWidth: 260,
            maxWidth: 400,
          }}
        >
          <Icono nombre="location_searching" size={20} color={c.texto3} />
          <input
            type="search"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Pallet, artículo o posición"
            aria-label="Buscar en el mapa"
            style={{ flex: 1, minWidth: 0, border: 0, outline: 0, background: 'transparent', fontSize: 14, color: c.texto }}
          />
        </label>
      </Encabezado>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => {
            setSectorId(null);
            setSeleccion(null);
          }}
          style={crumb}
        >
          Planta general
        </button>

        <AnimatePresence initial={false}>
        {sector && (
          <motion.span key="miga" {...pop} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icono nombre="chevron_right" size={18} color={c.texto4} />
            <span
              style={{
                padding: '7px 14px',
                borderRadius: 999,
                background: c.verdeSuave,
                border: `1px solid ${c.verde}`,
                color: c.verde,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {sector.nombre}
            </span>
          </motion.span>
        )}
        </AnimatePresence>

        <span style={{ flex: 1 }} />

        {!sector && (
          <span style={{ display: 'flex', gap: 6 }}>
            {(['plano', 'iso'] as const).map((v) => (
              <ChipBoton
                key={v}
                grupo="racks-vista"
                activo={vista === v}
                onClick={() => setVista(v)}
                style={{ height: 34, padding: '0 13px', fontSize: 12.5, border: `1px solid ${c.bordeFuerte}` }}
              >
                {v === 'plano' ? 'Plano' : '3D'}
              </ChipBoton>
            ))}
          </span>
        )}

        <AnimatePresence initial={false}>
        {(datos?.hits ?? 0) > 0 && (
          // La cuenta corre hasta el número nuevo cuando cambia la búsqueda:
          // se nota que es otro sin que el chip entero salte.
          <motion.span
            key="coincidencias"
            {...pop}
            style={{
              padding: '7px 14px',
              borderRadius: 999,
              background: c.ambarSuave,
              border: `1px solid ${c.ambar}`,
              color: c.ambar,
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            {datos!.hits === 1 ? (
              '1 posición coincide'
            ) : (
              <>
                <Numero valor={datos!.hits} /> posiciones coinciden
              </>
            )}
          </motion.span>
        )}
        </AnimatePresence>
      </div>

      <AnimatePresence initial={false}>
      {puedeOperar && (datos?.sinUbicar.length ?? 0) > 0 && (
        <motion.div key="sin-ubicar" {...aparecer} style={aviso('warn')}>
          <strong>
            {datos!.sinUbicar.length === 1
              ? '1 pallet ingresó y todavía no tiene lugar'
              : `${datos!.sinUbicar.length} pallets ingresaron y todavía no tienen lugar`}
            :
          </strong>{' '}
          <span style={{ fontFamily: mono }}>
            {datos!.sinUbicar.slice(0, 8).map((p) => p.id).join(', ')}
          </span>
          {datos!.sinUbicar.length > 8 && ` y ${datos!.sinUbicar.length - 8} más`}.
          {onUbicar && (
            <div style={{ marginTop: 10 }}>
              <button type="button" onClick={() => onUbicar()} style={{ ...botonPrimario, minHeight: 44, padding: '0 16px' }}>
                Ubicarlos ahora
              </button>
            </div>
          )}
        </motion.div>
      )}
      </AnimatePresence>

      {error ? (
        <PanelError mensaje={error} onReintentar={recargar} />
      ) : cargando && !datos ? (
        <Cargando texto="Dibujando la planta…" alto={280} />
      ) : (
        <div
          id="wms-mapa-grid"
          ref={grilla}
          style={{
            // Para el `popLayout` del detalle: el que se va flota sobre la
            // grilla mientras se cierra.
            position: 'relative',
            display: 'grid',
            // Sin posición elegida no hay panel al costado, así que tampoco hay
            // segunda columna: el mapa ocupa el ancho entero. En celular manda
            // la regla de `global.css`, que fuerza una sola columna siempre.
            gridTemplateColumns: posicion ? 'minmax(0,1.75fr) minmax(300px,1fr)' : 'minmax(0,1fr)',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <div ref={lienzo} style={{ ...tarjeta, padding: 16, minWidth: 0 }}>
            {/* El dibujo del plano no se anima por dentro (se redibuja con cada
                refresco); lo que se anima es el paso entre plano, 3D y sector.
                El sector nace en círculo desde el punto del plano donde se lo
                tocó y, al volver a la planta, se cierra hacia ahí. */}
            <AnimatePresence mode="wait" initial={false}>
            {sector ? (
              <motion.div key={`sector-${sector.id}`} {...revelar(...origenSector)}>
                <VistaSector
                  sector={sector}
                  seleccion={seleccion}
                  onPosicion={(codigo) => {
                    elegirPosicion(codigo);
                    setVerMovs(false);
                  }}
                />
              </motion.div>
            ) : (
            <motion.div key={vista} {...aparecer}>
            {vista === 'iso' ? (
              /*
                Los dos motores de la vista 3D reciben **los mismos siete
                props** -es lo que permite cambiar uno por el otro sin tocar
                nada de acá- así que se arman una sola vez, arriba, en
                `vista3d`. Escritos dos veces terminarían divergiendo.

                Con WebGL va la escena de `three`; sin WebGL, el dibujo
                isométrico en SVG, que es el que venía andando. En el depósito
                hay celulares viejos y una pantalla en blanco no le dice nada a
                un operario.
              */
              hayWebGL() ? (
                <div style={{ position: 'relative' }}>
                  <Suspense fallback={<Cargando texto="Levantando el depósito…" alto={280} />}>
                    <Escena3d {...vista3d} />
                  </Suspense>

                  {/* El tercer camino: WebGL arrancó y el contexto se perdió
                      después. Tapa la escena en vez de dejarla en blanco sin
                      explicación; se saca sola si el navegador restaura el
                      contexto (`contextoPerdido` en el estado de más arriba). */}
                  <AnimatePresence>
                  {contextoPerdido && (
                    <motion.div
                      key="contexto"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 16,
                        background: 'rgba(255,255,255,.85)',
                        borderRadius: 12,
                      }}
                    >
                      <div style={{ ...aviso('warn'), maxWidth: 360, textAlign: 'center' }}>
                        <strong>Se perdió la conexión con la tarjeta gráfica.</strong>
                        <div style={{ marginTop: 6 }}>
                          El navegador suele recuperarla sola en unos segundos. Si el mapa no vuelve,
                          recargá la página.
                        </div>
                        <button
                          type="button"
                          onClick={() => window.location.reload()}
                          style={{ ...botonSecundario, marginTop: 10 }}
                        >
                          Recargar
                        </button>
                      </div>
                    </motion.div>
                  )}
                  </AnimatePresence>
                </div>
              ) : (
                <Isometrico {...vista3d} />
              )
            ) : (
              <PlanoGeneral
                predio={datos?.predio ?? { nombre: 'Depósito', ancho: 60, largo: 40 }}
                naves={datos?.naves ?? []}
                sectores={sectores}
                muelles={datos?.muelles ?? []}
                caminos={datos?.caminos ?? []}
                onSector={(id) => {
                  entrarASector(id);
                  setSeleccion(null);
                }}
              />
            )}
            </motion.div>
            )}
            </AnimatePresence>
          </div>

          {/*
            El panel de detalle **existe sólo cuando hay una posición elegida**.
            Sin posición no hay segunda columna y el mapa se lleva todo el ancho:
            es lo que le da el protagonismo a la vista 3D (pedido del usuario,
            2026-09-07).

            Lo que vivía acá cuando no había nada elegido era la lista de
            «Ocupación por sector», y se saca porque **la escena ya la trae
            adentro**: el menú SECTORES de `Escena3d` lista los mismos sectores
            con su color y su porcentaje, y además enfoca. Lo único que la lista
            hacía y el menú no es **entrar** al sector; para eso quedan los dos
            caminos que ya existían: tocar un sector en el plano 2D y abrir una
            posición desde la escena, que entra a su sector (ver `vista3d`).
          */}
          {/* Con `key` en la posición: tocar otra vuelve a hacer entrar el
              panel, así se nota que el detalle es de la nueva. En el celular
              cae debajo del sector y sube; en la PC aparece al costado. */}
          <AnimatePresence mode="popLayout">
          {posicion && (
            <motion.div
              key={posicion.codigo}
              {...(celular ? subir : revelar('0px', origenDetalle))}
              ref={detalle}
              style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}
            >
              {/* Los movimientos nacen del botón que los abre (abajo a la
                  izquierda) y el detalle vuelve desde la flecha de volver. */}
              <AnimatePresence mode="wait" initial={false}>
              {verMovs ? (
                <motion.div key="movs" {...revelar('70px', 'calc(100% - 22px)')}>
                  <Movimientos codigo={posicion.codigo} onVolver={() => setVerMovs(false)} />
                </motion.div>
              ) : (
                <motion.div key="detalle" {...revelar('16px', '16px')} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.09em', textTransform: 'uppercase', color: c.texto3 }}>
                      Posición
                    </div>
                    <div style={{ marginTop: 4, fontFamily: mono, fontSize: 22, fontWeight: 500, color: c.texto }}>
                      {posicion.codigo}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, color: c.texto2 }}>
                      {sector?.nombre} ·{' '}
                      {posicion.zona
                        ? `zona de piso ${posicion.codigo.split('-')[0]}`
                        : `rack ${posicion.codigo.split('-')[0]} · posición ${posicion.codigo.split('-')[1]}`}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* La zona de piso no tiene niveles: se listan todos sus pallets. */}
                    {posicion.zona?.length === 0 && (
                      <div style={{ fontSize: 13, color: c.texto3 }}>Zona de piso sin pallets.</div>
                    )}
                    {(posicion.zona?.map((z) => ({ ...z, nivel: null, ocupado: true })) ?? posicion.niveles).map((n, i) => (
                      <motion.div
                        key={n.pallet ?? n.nivel}
                        {...fila(i)}
                        style={{
                          border: `1px solid ${c.borde}`,
                          borderRadius: 12,
                          padding: '12px 14px',
                          background: n.ocupado ? c.panel : '#fafbfc',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: 8,
                              display: 'grid',
                              placeItems: 'center',
                              background: c.fondo,
                              border: `1px solid ${c.borde}`,
                              fontFamily: mono,
                              fontSize: 12,
                              fontWeight: 600,
                              color: c.texto2,
                            }}
                          >
                            {n.nivel === null ? 'Z' : `N${n.nivel}`}
                          </span>
                          <span style={{ flex: 1, minWidth: 0, fontFamily: mono, fontSize: 13, fontWeight: 500, color: c.texto }}>
                            {n.ocupado && n.pallet && onVerPallet && puedeVerPallet ? (
                              <button type="button" onClick={() => onVerPallet(n.pallet!)} title="Ver la ficha del pallet" style={{ ...enlace, fontSize: 13 }}>
                                {n.pallet}
                              </button>
                            ) : n.ocupado ? (
                              n.pallet
                            ) : (
                              'Nivel libre'
                            )}
                          </span>
                          <span style={chip(n.ocupado ? 'ok' : 'neu')}>{n.ocupado ? 'Ocupado' : 'Libre'}</span>
                        </div>
                        <div style={{ marginTop: 6, paddingLeft: 36, fontSize: 12.5, color: c.texto2 }}>
                          {n.ocupado ? n.articulo : 'Disponible para asignar'}
                        </div>
                        <div style={{ marginTop: 2, paddingLeft: 36, fontSize: 12, color: c.texto3 }}>
                          {n.ocupado ? `${n.tipo === 'completo' ? 'pallet entero' : `${n.piezas} piezas`} · ingreso ${n.ingreso} · ${n.dias} días` : '—'}
                        </div>
                        {/* Los pallets se mueven en un solo lugar, Ubicar pallets: acá
                            se lo manda allá ya elegido, y sólo queda tocar el
                            hueco donde va. */}
                        {puedeOperar && onUbicar && n.ocupado && n.pallet && (
                          <button
                            type="button"
                            onClick={() => onUbicar(n.pallet!)}
                            // En el celular, a lo ancho de su tarjeta: suelto y a medio ancho parecía caído.
                            className="w-[calc(100%-36px)] justify-center tablet:w-auto tablet:justify-start"
                            style={{ ...botonSecundario, minHeight: 44, padding: '0 16px', marginTop: 10, marginLeft: 36 }}
                          >
                            <Icono nombre="move_down" size={18} color={c.texto2} />
                            Mover en Ubicar pallets
                          </button>
                        )}
                      </motion.div>
                    ))}
                  </div>

                  {/* En el celular los dos se reparten el renglón; en 360 cada uno
                      caía solo y a medio ancho en su propio renglón. */}
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => setVerMovs(true)} className="flex-1 justify-center whitespace-nowrap py-0 px-3 tablet:flex-none tablet:justify-start tablet:px-[18px]" style={{ ...botonSecundario, minHeight: 44, padding: undefined }}>
                      <Icono nombre="history" size={19} color={c.texto2} />
                      Movimientos
                    </button>
                    {onVaciarRack && rackDeLaPosicion && (
                      <button
                        type="button"
                        onClick={() => onVaciarRack(rackDeLaPosicion.id)}
                        className="flex-1 justify-center whitespace-nowrap py-0 px-3 tablet:flex-none tablet:justify-start tablet:px-[18px]"
                        style={{ ...botonSecundario, minHeight: 44, padding: undefined }}
                      >
                        <Icono nombre="shelves" size={19} color={c.texto2} />
                        Vaciar rack
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
              </AnimatePresence>
            </motion.div>
          )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
